import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatMoney } from "@/components/KPICard";
import { formatDMY } from "@/lib/dates";
import { toast } from "sonner";

/**
 * 1-click renewal: creates a new contract with the same tenant + property,
 * starting the day AFTER the old one ends, for 12 more months.
 * User can override start/end/monthly/adjustment before confirming.
 */
export default function RenewContractDialog({ open, onOpenChange, contract, onRenewed }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthly, setMonthly] = useState("");
  const [adjustmentPct, setAdjustmentPct] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!contract) return;
    const oldEnd = new Date(contract.end_date + "T00:00:00");
    const newStart = new Date(oldEnd);
    newStart.setDate(newStart.getDate() + 1);
    const newEnd = new Date(newStart);
    newEnd.setFullYear(newEnd.getFullYear() + 1);
    const toIso = (d) => d.toISOString().slice(0, 10);
    setStartDate(toIso(newStart));
    setEndDate(toIso(newEnd));
    setMonthly(String(contract.monthly_amount));
    setAdjustmentPct("");
    setNotes("");
  }, [contract]);

  if (!contract) return null;

  const applyAdjustment = () => {
    const pct = parseFloat(adjustmentPct);
    if (Number.isNaN(pct)) return;
    const base = parseFloat(String(contract.monthly_amount));
    setMonthly((base * (1 + pct / 100)).toFixed(2));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        start_date: startDate,
        end_date: endDate,
        monthly_amount: parseFloat(monthly),
      };
      if (notes) payload.notes = notes;
      await api.post(`/contracts/${contract.id}/renew`, payload);
      toast.success("Contrato renovado. Se generaron los pagos del nuevo período.");
      onOpenChange(false);
      onRenewed?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="renew-contract-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Renovar contrato · 1 clic</DialogTitle>
          <DialogDescription>
            Se crea un contrato NUEVO con el mismo inquilino y propiedad. El contrato actual queda marcado como <strong>finalizado</strong> (sus pagos ya cobrados se preservan).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg border border-border/60 bg-secondary/30 p-3 text-xs space-y-1">
            <div>Contrato actual: {formatDMY(contract.start_date)} → {formatDMY(contract.end_date)}</div>
            <div>Renta actual: <strong className="num">{formatMoney(contract.monthly_amount, contract.currency)}/mes</strong></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nuevo inicio</Label>
              <Input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="renew-start-date" />
            </div>
            <div>
              <Label>Nuevo fin</Label>
              <Input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="renew-end-date" />
            </div>
          </div>

          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
            <Label className="text-[11px] uppercase tracking-widest text-emerald-500 font-semibold">
              📈 Ajuste de renta (opcional)
            </Label>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Aumentar %</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="ej. 15"
                  value={adjustmentPct}
                  onChange={(e) => setAdjustmentPct(e.target.value)}
                  data-testid="renew-adjustment-pct"
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={applyAdjustment} data-testid="renew-apply-adjustment">
                Aplicar
              </Button>
            </div>
          </div>

          <div>
            <Label>Nueva renta mensual ({contract.currency})</Label>
            <Input
              type="number"
              step="0.01"
              required
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              className="h-11 text-base"
              data-testid="renew-monthly-amount"
            />
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="renew-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting} data-testid="renew-confirm-btn">
              {submitting ? "Renovando…" : "Renovar contrato"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
